import {
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import {
  buildPianoTypingMap,
  PIANO_WHITE_OFFSETS,
} from "../lib/pianoTyping";

/** Semitone offsets from C for white keys drawn on the piano (one octave + top C). */
const DRAW_WHITE_OFFSETS = PIANO_WHITE_OFFSETS.slice(0, 8);

export type MusicalKeyboardProps = {
  /** Typing only fires while focus is inside this host (synth panel / author modal). */
  focusHostRef: RefObject<HTMLElement | null>;
  /** Base MIDI note for the lowest C of the visible range (default 48 = C3). */
  baseMidi?: number;
  /** Octaves to draw (default 2). */
  octaves?: number;
  /**
   * `mono` — synth legato: releasing a key while others are held re-triggers the
   * last held note without a noteOff (single voice).
   * `poly` — each key sends its own noteOff (SoundFont / drums).
   */
  voiceMode?: "mono" | "poly";
  onNoteOn: (note: number, velocity: number) => void;
  onNoteOff: (note: number) => void;
  /** Fired when Z/X changes the typing octave. */
  onOctaveChange?: (shift: number) => void;
};

function buildWhiteNotes(baseMidi: number, octaves: number): number[] {
  const whiteNotes: number[] = [];
  for (let o = 0; o < octaves; o++) {
    for (const offset of DRAW_WHITE_OFFSETS.slice(0, 7)) {
      whiteNotes.push(baseMidi + o * 12 + offset);
    }
  }
  whiteNotes.push(baseMidi + octaves * 12);
  return whiteNotes;
}

function buildBlackKeys(
  baseMidi: number,
  octaves: number,
  whiteNotes: number[],
): Array<{ note: number; left: string }> {
  const blackPattern = [1, 3, -1, 6, 8, 10];
  const keys: Array<{ note: number; left: string }> = [];
  for (let o = 0; o < octaves; o++) {
    for (const offset of blackPattern) {
      if (offset < 0) {
        continue;
      }
      const note = baseMidi + o * 12 + offset;
      const whiteIndex = whiteNotes.findIndex(n => n > note) - 1;
      if (whiteIndex >= 0) {
        keys.push({
          note,
          left: `calc(${((whiteIndex + 1) / whiteNotes.length) * 100}% - 0.475rem)`,
        });
      }
    }
  }
  return keys;
}

/**
 * On-screen piano + QWERTY musical typing.
 * Held / drag paint uses refs + classList so note spam does not re-render the tree.
 */
export function MusicalKeyboard({
  focusHostRef,
  baseMidi = 48,
  octaves = 2,
  voiceMode = "mono",
  onNoteOn,
  onNoteOff,
  onOctaveChange,
}: MusicalKeyboardProps) {
  const pianoRef = useRef<HTMLDivElement>(null);
  const heldRef = useRef(new Set<number>());
  const heldOrderRef = useRef<number[]>([]);
  const draggingRef = useRef(false);
  const octaveShiftRef = useRef(0);
  const keyToNoteRef = useRef(buildPianoTypingMap(baseMidi, 0));
  const voiceModeRef = useRef(voiceMode);
  const callbacksRef = useRef({ onNoteOn, onNoteOff, onOctaveChange });

  callbacksRef.current = { onNoteOn, onNoteOff, onOctaveChange };
  voiceModeRef.current = voiceMode;

  const whiteNotes = buildWhiteNotes(baseMidi, octaves);
  const blackKeys = buildBlackKeys(baseMidi, octaves, whiteNotes);

  useLayoutEffect(() => {
    keyToNoteRef.current = buildPianoTypingMap(baseMidi, octaveShiftRef.current);
  }, [baseMidi]);

  function paintNote(note: number, active: boolean): void {
    const el = pianoRef.current?.querySelector(`[data-note="${note}"]`);
    el?.classList.toggle("active", active);
  }

  function noteOn(note: number): void {
    if (heldRef.current.has(note)) {
      return;
    }
    heldRef.current.add(note);
    heldOrderRef.current.push(note);
    paintNote(note, true);
    callbacksRef.current.onNoteOn(note, 0.9);
  }

  function noteOff(note: number): void {
    if (!heldRef.current.has(note)) {
      return;
    }
    heldRef.current.delete(note);
    const index = heldOrderRef.current.lastIndexOf(note);
    if (index >= 0) {
      heldOrderRef.current.splice(index, 1);
    }
    paintNote(note, false);

    // Poly instruments (SF2, drums): always release the exact key.
    if (voiceModeRef.current === "poly") {
      callbacksRef.current.onNoteOff(note);
      return;
    }

    // Mono synth legato: only noteOff when nothing remains; otherwise re-attack
    // the last still-held key without silencing the voice mid-chord.
    if (heldOrderRef.current.length === 0) {
      callbacksRef.current.onNoteOff(note);
      return;
    }
    const next = heldOrderRef.current[heldOrderRef.current.length - 1]!;
    callbacksRef.current.onNoteOn(next, 0.9);
  }

  function releaseAll(): void {
    if (heldRef.current.size === 0) {
      return;
    }
    const notes = [...heldRef.current];
    for (const note of notes) {
      paintNote(note, false);
    }
    heldRef.current.clear();
    heldOrderRef.current.length = 0;
    if (voiceModeRef.current === "poly") {
      for (const note of notes) {
        callbacksRef.current.onNoteOff(note);
      }
      return;
    }
    callbacksRef.current.onNoteOff(notes[notes.length - 1] ?? 0);
  }

  function setOctaveShift(shift: number): void {
    const next = Math.max(-2, Math.min(3, shift));
    octaveShiftRef.current = next;
    keyToNoteRef.current = buildPianoTypingMap(baseMidi, next);
    callbacksRef.current.onOctaveChange?.(next);
  }

  function isFocused(): boolean {
    const host = focusHostRef?.current;
    if (!host) {
      return false;
    }
    const active = document.activeElement;
    return active === host || (active instanceof Node && host.contains(active));
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!isFocused() || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        target
        && (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA")
      ) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) {
          setOctaveShift(octaveShiftRef.current - 1);
        }
        return;
      }
      if (key === "x") {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) {
          setOctaveShift(octaveShiftRef.current + 1);
        }
        return;
      }
      const note = keyToNoteRef.current.get(key);
      if (note === undefined) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) {
        return;
      }
      noteOn(note);
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (!isFocused()) {
        return;
      }
      const note = keyToNoteRef.current.get(event.key.toLowerCase());
      if (note === undefined) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      noteOff(note);
    };

    const onBlur = (): void => releaseAll();

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    window.addEventListener("blur", onBlur);
    return () => {
      releaseAll();
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("blur", onBlur);
    };
  }, [baseMidi, focusHostRef]);

  return (
    <div
      ref={pianoRef}
      className="piano relative h-16 touch-none select-none"
      role="group"
      aria-label="Piano keyboard"
      onPointerUp={() => {
        draggingRef.current = false;
      }}
      onPointerLeave={() => {
        if (draggingRef.current) {
          draggingRef.current = false;
          releaseAll();
        }
      }}
    >
      <div className="piano-whites flex h-full gap-px">
        {whiteNotes.map(note => (
          <KeyButton
            key={note}
            note={note}
            black={false}
            onPointerDown={event => {
              event.preventDefault();
              draggingRef.current = true;
              (event.currentTarget as HTMLButtonElement).setPointerCapture(event.pointerId);
              noteOn(note);
              focusHostRef?.current?.focus();
            }}
            onPointerEnter={() => {
              if (draggingRef.current) {
                noteOn(note);
              }
            }}
            onPointerUp={() => {
              noteOff(note);
              draggingRef.current = false;
            }}
            onPointerCancel={() => {
              noteOff(note);
              draggingRef.current = false;
            }}
          />
        ))}
      </div>
      <div className="piano-blacks pointer-events-none absolute inset-x-0 top-0 bottom-[35%]">
        {blackKeys.map(({ note, left }) => (
          <KeyButton
            key={note}
            note={note}
            black
            style={{ left }}
            onPointerDown={event => {
              event.preventDefault();
              draggingRef.current = true;
              (event.currentTarget as HTMLButtonElement).setPointerCapture(event.pointerId);
              noteOn(note);
              focusHostRef?.current?.focus();
            }}
            onPointerEnter={() => {
              if (draggingRef.current) {
                noteOn(note);
              }
            }}
            onPointerUp={() => {
              noteOff(note);
              draggingRef.current = false;
            }}
            onPointerCancel={() => {
              noteOff(note);
              draggingRef.current = false;
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function formatOctaveLabel(shift: number): string {
  return `octave ${shift >= 0 ? `+${shift}` : String(shift)} (Z/X)`;
}

function KeyButton({
  note,
  black,
  style,
  onPointerDown,
  onPointerEnter,
  onPointerUp,
  onPointerCancel,
}: {
  note: number;
  black: boolean;
  style?: CSSProperties;
  onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerEnter: () => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
}) {
  return (
    <button
      type="button"
      className={
        black
          ? "piano-key black pointer-events-auto absolute top-0 z-[1] h-full w-[0.95rem] rounded-b-[3px] border border-[#0b0e14] bg-gradient-to-b from-slate-700 to-slate-950 p-0 hover:bg-slate-800"
          : "piano-key white min-w-0 flex-1 rounded-b-[3px] border border-[#0b0e14] bg-gradient-to-b from-slate-50 to-slate-200 p-0 hover:bg-white"
      }
      data-note={note}
      tabIndex={-1}
      style={style}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    />
  );
}
